import { useMutation } from '@apollo/client';
import { zodResolver } from '@hookform/resolvers/zod';
// eslint-disable-next-line lodash/import-scope
import { upperFirst } from 'lodash';
import { EditIcon, PlusIcon } from 'lucide-react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { QUERY_ROOT_ID, cacheUtils } from '@eluve/apollo-client';
import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  Divider,
  FormField,
  Input,
  Label,
  NewButton,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Textarea,
  VStack,
  useToast,
} from '@eluve/components';
import { DbEnums } from '@eluve/graphql-types';
import { graphql } from '@eluve/graphql.tada';
import {
  useUserDisplayFromSession,
  useUserIdFromSession,
} from '@eluve/session-helpers';

import { SiteBannerItem } from './SiteBannerItem';
import {
  siteBannerFragment,
  siteBannerPublicDataFragment,
  siteBannersFragment,
} from './operations';

const siteBannerFormSchema = z.object({
  bannerType: z.nativeEnum(DbEnums.SiteBannerTypes),
  title: z.string().min(1, 'Site banner title is required'),
  message: z.string().min(1, 'Site banner message is required'),
});

const updateSiteBannerMutation = graphql(
  `
    mutation updateSiteBanner(
      $siteBannerId: uuid!
      $bannerType: SiteBannerTypesEnum!
      $title: String!
      $message: String!
    ) {
      updateSiteBannersByPk(
        pkColumns: { id: $siteBannerId }
        _set: { bannerType: $bannerType, title: $title, message: $message }
      ) {
        ...SiteBannerPublicData
      }
    }
  `,
  [siteBannerPublicDataFragment],
);

const createSiteBannerMutation = graphql(
  `
    mutation createSiteBanner(
      $bannerType: SiteBannerTypesEnum!
      $title: String!
      $message: String!
    ) {
      insertSiteBannersOne(
        object: { bannerType: $bannerType, title: $title, message: $message }
      ) {
        ...SiteBanner
      }
    }
  `,
  [siteBannerFragment],
);

type SiteBannerFormValues = z.infer<typeof siteBannerFormSchema>;

// Define a complete set of default values
const emptyDefaultValues: SiteBannerFormValues = {
  bannerType: 'INFO',
  title: '',
  message: '',
};

interface CreateOrUpdateSiteBannerActionProps {
  siteBannerId?: string;
  defaultValues?: SiteBannerFormValues;
}

export const CreateOrUpdateSiteBannerAction: React.FC<
  CreateOrUpdateSiteBannerActionProps
> = ({ siteBannerId, defaultValues }) => {
  const toast = useToast();
  const userId = useUserIdFromSession();
  const userDetails = useUserDisplayFromSession();

  const [open, setOpen] = useState(false);

  const { control, formState, handleSubmit, reset, watch } =
    useForm<SiteBannerFormValues>({
      resolver: zodResolver(siteBannerFormSchema),
      defaultValues: defaultValues ?? emptyDefaultValues,
    });

  const [updateSiteBanner] = useMutation(updateSiteBannerMutation, {
    onCompleted: ({ updateSiteBannersByPk }) => {
      if (!updateSiteBannersByPk) {
        return;
      }

      reset(updateSiteBannersByPk);
      setOpen(false);
    },
    onError: () => {
      toast.error('Failed to update site banner');
    },
  });

  const [createSiteBanner] = useMutation(createSiteBannerMutation, {
    optimisticResponse: (data) => ({
      insertSiteBannersOne: {
        __typename: 'SiteBanners' as const,
        id: 'optimistic-id',
        bannerType: data.bannerType,
        title: data.title,
        message: data.message,
        isActive: false,
        createdBy: userId,
        user: {
          __typename: 'Users' as const,
          firstName: userDetails.firstName,
          lastName: userDetails.lastName,
          email: userDetails.email,
          id: userId,
        },
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      },
    }),
    update: (_cache, { data }) => {
      cacheUtils.updateFragment(
        {
          fragment: siteBannersFragment,
          key: { id: QUERY_ROOT_ID },
        },
        (existing) => {
          if (!existing || !data?.insertSiteBannersOne) {
            return existing;
          }
          return {
            siteBanners: [...existing.siteBanners, data.insertSiteBannersOne],
          };
        },
      );
    },
    onCompleted: ({ insertSiteBannersOne }) => {
      if (!insertSiteBannersOne) {
        return;
      }
      reset({});
      setOpen(false);
    },
    onError: () => {
      toast.error('Failed to create site banner');
    },
  });

  const handleCreateOrUpdateSiteBanner = handleSubmit((data) => {
    if (siteBannerId) {
      return updateSiteBanner({
        variables: {
          siteBannerId,
          bannerType: data.bannerType,
          title: data.title,
          message: data.message,
        },
      });
    } else {
      return createSiteBanner({
        variables: {
          bannerType: data.bannerType,
          title: data.title,
          message: data.message,
        },
      });
    }
  });

  const handleOpenChange = (open: boolean) => {
    setOpen(open);
    reset(defaultValues);
  };

  const handleCancel = () => {
    setOpen(false);
  };

  const bannerType = watch('bannerType');
  const title = watch('title');
  const message = watch('message');

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        {siteBannerId ? (
          <Button variant="outline" size="icon">
            <EditIcon className="size-4" />
          </Button>
        ) : (
          <Button>
            <PlusIcon className="mr-2 size-4" />
            Create Site Banner
          </Button>
        )}
      </DialogTrigger>
      <DialogContent>
        <div className="flex gap-3">
          <EditIcon className="size-6" />
          <DialogHeader>
            <DialogTitle className="font-normal tracking-normal text-text-secondary">
              {siteBannerId ? 'Update Site Banner' : 'Create site banner'}
            </DialogTitle>
          </DialogHeader>
        </div>

        <Label>
          <VStack>
            Site banner type
            <FormField
              control={control}
              name="bannerType"
              render={({ field }) => (
                <Select onValueChange={field.onChange} value={field.value}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select banner type" />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.values(DbEnums.SiteBannerTypes).map((type) => (
                      <SelectItem key={type} value={type}>
                        {upperFirst(type.toLowerCase())}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            />
          </VStack>
        </Label>

        <Label>
          <VStack>
            Site banner title
            <FormField
              control={control}
              name="title"
              render={({ field }) => (
                <Input placeholder="Banner Title" {...field} />
              )}
            />
          </VStack>
        </Label>

        <Label>
          <VStack>
            Site banner message
            <FormField
              control={control}
              name="message"
              render={({ field }) => (
                <Textarea placeholder="Banner Message" {...field} />
              )}
            />
          </VStack>
        </Label>

        <Divider />

        <SiteBannerItem
          bannerType={bannerType}
          message={message || 'Body'}
          title={title || 'Title'}
        />

        <DialogFooter>
          <NewButton type="outline" onClick={handleCancel} text="Cancel" />
          <NewButton
            onClick={handleCreateOrUpdateSiteBanner}
            disabled={
              formState.isSubmitting || !formState.isValid || !formState.isDirty
            }
            text={siteBannerId ? 'Update' : 'Create'}
          />
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
